/*
 * Registro do Service Worker.
 *
 * Estratégia:
 *  - Só registra em produção (NODE_ENV === "production") OU explicitamente
 *    via REACT_APP_FORCE_SW=1 para testes locais com `serve -s build`.
 *  - Só registra em HTTPS ou localhost (SW exige contexto seguro).
 *  - Emite um CustomEvent("pharuerp:update-available") quando uma nova
 *    versão do SW está instalada e aguardando ativação — UI pode
 *    capturar e mostrar "Atualizar agora".
 */

const isLocalhost = Boolean(
  typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "[::1]" ||
      /^127(?:\.\d{1,3}){3}$/.test(window.location.hostname))
);

function broadcastUpdate(registration) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("pharuerp:update-available", { detail: { registration } })
  );
}

function watchUpdates(registration) {
  if (!registration) return;
  if (registration.waiting) broadcastUpdate(registration);
  registration.addEventListener("updatefound", () => {
    const installing = registration.installing;
    if (!installing) return;
    installing.addEventListener("statechange", () => {
      if (installing.state === "installed" && navigator.serviceWorker.controller) {
        broadcastUpdate(registration);
      }
    });
  });
}

export function register() {
  if (typeof window === "undefined") return;
  const env = process.env.NODE_ENV;
  const force = process.env.REACT_APP_FORCE_SW === "1";
  if (env !== "production" && !force) return;
  if (!("serviceWorker" in navigator)) return;

  // Em produção (Vercel) o protocolo é HTTPS. Em localhost o registro é
  // permitido para facilitar testes via `serve -s build`.
  if (window.location.protocol !== "https:" && !isLocalhost) return;

  window.addEventListener("load", () => {
    const swUrl = `${process.env.PUBLIC_URL || ""}/service-worker.js`;
    navigator.serviceWorker
      .register(swUrl)
      .then((registration) => {
        watchUpdates(registration);
        // Recarrega ao trocar de controller (após SKIP_WAITING) — mas SÓ quando
        // a troca vier de uma ATUALIZAÇÃO de versão.
        //
        // Sem a guarda abaixo, o PRIMEIRO acesso de cada dispositivo se
        // recarregava sozinho: na visita inicial não há controller, o SW novo
        // faz `skipWaiting()` (service-worker.js:40) + `clients.claim()`
        // (service-worker.js:54) e assume a página aberta, o que dispara
        // `controllerchange` e caía neste reload. Efeito: o usuário baixava o
        // bundle, a página reiniciava do zero e o `warmupAccessApi()` em voo
        // (que estava acordando o backend Render Free) era abortado — dobrando
        // o tempo até a tela e favorecendo o timeout logo em seguida.
        // O mesmo cuidado já existia em `watchUpdates` (linha 34).
        const tinhaControllerAoRegistrar = Boolean(
          navigator.serviceWorker.controller
        );
        let refreshing = false;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          // Primeira instalação neste dispositivo: nada a recarregar, a página
          // já está rodando a versão mais nova que acabou de ser baixada.
          if (!tinhaControllerAoRegistrar) return;
          if (refreshing) return;
          refreshing = true;
          window.location.reload();
        });
      })
      .catch((err) => {
        // Não bloqueia a aplicação se o SW falhar — apenas reporta.
        console.warn("[PharuERP] Falha ao registrar service worker:", err);
      });
  });
}

export function unregister() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.ready
    .then((registration) => registration.unregister())
    .catch(() => null);
}

export function applyUpdate(registration) {
  if (!registration || !registration.waiting) return;
  registration.waiting.postMessage({ type: "SKIP_WAITING" });
}
